import getRandomString from "../getRandomString";
import log from "../log";

type Id = string;
export interface EventTopicBase {
	[key: string]: {
		props: any;
	};
}
/**
 * This class is designed as a communication bridge between two separate parts of a codebase.
 *
 * Developer A creates a new topic and calls `emit` in one or more places where the event fires. For example, a topic for in-app push notification receives would emit a message every time a push notification is received.
 *
 * Developer B wants to know when a new push notification is received. Instead of having to rewrite the code for push notification receiving or passing functions up, Developer B just defines a listener for this topic using `on` anywhere in the codebase.
 *
 * Developer A and Developer B don't need to know about each other.
 */
class EventTopic<TEvents extends EventTopicBase> {
	private readonly listeners: {
		[K in keyof TEvents]?: Record<Id, (props: TEvents[K]["props"]) => void | Promise<void>>;
	} = {};
	on<K extends keyof TEvents>(
		event: K,
		listener: (props: TEvents[K]["props"]) => void | Promise<void>,
	): {
		unsubscribe: () => void;
	} {
		const id = getRandomString();
		if (!this.listeners[event]) {
			this.listeners[event] = {};
		}

		// Nullish coalescence to fix TS errors
		const topicListeners = this.listeners[event] ?? {};
		topicListeners[id] = listener;

		return {
			unsubscribe: () => {
				delete this.listeners[event]?.[id];
			},
		};
	}
	emit<K extends keyof TEvents>(event: K, props: TEvents[K]["props"]): void {
		const listeners = this.listeners[event];
		for (const listener of Object.values(listeners || {})) {
			try {
				listener(props);
			} catch (err) {
				log.error({ err }, "Error in listener");
			}
		}
	}
	async emitAsync<K extends keyof TEvents>(
		event: K,
		props: TEvents[K]["props"],
	): Promise<{
		didError: boolean;
	}> {
		const listeners = this.listeners[event];
		let didError = false;
		await Promise.all(
			Object.values(listeners || {}).map(async (listener) => {
				try {
					await listener(props);
				} catch (err) {
					didError = true;
					log.error({ err }, "Error in listener");
				}
			}),
		);
		return {
			didError,
		};
	}
}
export default EventTopic;
